import { createListenerMiddleware } from '@reduxjs/toolkit'
import { AppRootState } from 'src/store/store'
import {
  CERTIFICATE_POLL_MAX_ATTEMPTS,
  certificatePollDelayMs,
} from 'src/v3/classroom-v2/utils/certificate-poll'
import type { EventCertificate, EventCertificatePoll } from './types'
import {
  eventCertificatePollCancelled,
  eventCertificatePollExhausted,
  eventCertificatePollRequested,
  fetchEventProgress,
  pollEventCertificate,
  refreshEvents,
  selectEventCertificatePoll,
  selectEventProgress,
  selectEventsStatus,
  selectEventV2ById,
} from './events-v2.slice'
import { isEventAttentionStale } from './utils/session-attention'

export let listenerMiddleware = createListenerMiddleware()

// The events payload carries per-user progress fields (Requires attention, Completed). Keep it
// in step with /progress however progress moved, not just after specific buttons: refresh when
// a fresh /progress disagrees with the cached event.
listenerMiddleware.startListening({
  actionCreator: fetchEventProgress.fulfilled,
  effect: (action, listenerApi) => {
    let eventId = action.meta.arg
    let state = listenerApi.getState() as AppRootState
    // The reducer drops superseded responses; compare against what it actually stored.
    let progress = selectEventProgress(eventId)(state)
    let event = selectEventV2ById(eventId)(state)
    if (selectEventsStatus(state) !== 'fulfilled' || !event || !progress) return
    if (isEventAttentionStale(event, progress)) {
      listenerApi.dispatch(refreshEvents())
    }
  },
})

let isCertificateResolved = (cert: EventCertificate): boolean =>
  cert.kind === 'issued' ||
  cert.kind === 'notEarned' ||
  cert.kind === 'notApplicable'

type EventCertificatePollMove =
  | { action: 'ask' }
  | { action: 'waitThenAsk'; delayMs: number }
  | { action: 'stop' }
  | { action: 'exhaust' }

let eventCertificatePollNextMove = (
  poll: EventCertificatePoll | null,
): EventCertificatePollMove => {
  if (poll === null) return { action: 'stop' }
  if (poll.state === 'polling') return { action: 'ask' }
  if (poll.state === 'failed') return { action: 'stop' }
  if (isCertificateResolved(poll.certificate)) return { action: 'stop' }
  if (poll.attempts >= CERTIFICATE_POLL_MAX_ATTEMPTS)
    return { action: 'exhaust' }
  return {
    action: 'waitThenAsk',
    delayMs: certificatePollDelayMs(poll.attempts),
  }
}

let isEventCertificatePollEvent = (action: unknown) =>
  eventCertificatePollRequested.match(action) ||
  eventCertificatePollCancelled.match(action) ||
  pollEventCertificate.fulfilled.match(action) ||
  pollEventCertificate.rejected.match(action)

listenerMiddleware.startListening({
  matcher: isEventCertificatePollEvent,
  effect: async (action, listenerApi) => {
    listenerApi.cancelActiveListeners()

    let eventId =
      eventCertificatePollRequested.match(action) ||
      eventCertificatePollCancelled.match(action)
        ? action.payload
        : pollEventCertificate.fulfilled.match(action)
          ? action.meta.arg
          : (action as ReturnType<typeof pollEventCertificate.rejected>).meta
              .arg

    let move = eventCertificatePollNextMove(
      selectEventCertificatePoll(eventId)(
        listenerApi.getState() as AppRootState,
      ),
    )

    if (move.action === 'stop') return
    if (move.action === 'exhaust') {
      listenerApi.dispatch(eventCertificatePollExhausted(eventId))
      return
    }

    if (move.action === 'waitThenAsk') {
      await listenerApi.delay(move.delayMs)
      if (
        eventCertificatePollNextMove(
          selectEventCertificatePoll(eventId)(
            listenerApi.getState() as AppRootState,
          ),
        ).action !== 'waitThenAsk'
      ) {
        return
      }
    }

    listenerApi.dispatch(pollEventCertificate(eventId))
  },
})
